
import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import PolicyText from '../components/PolicyText';
import { supabase } from '../lib/supabase';
import { Language } from '../translations';
import { getLocalizedPath } from '../lib/i18n';

interface SuccessProps {
    t: any;
    lang?: Language;
}

const Success: React.FC<SuccessProps> = ({ t, lang = 'es' }) => {
    const [searchParams] = useSearchParams();
    const bookingId = searchParams.get('booking_id');
    const paymentUuid = searchParams.get('payment_uuid');
    const provider = searchParams.get('provider');
    const [isCheckingVirtualPos, setIsCheckingVirtualPos] = useState(provider === 'virtualpos');

    const typeParam = searchParams.get('type') || searchParams.get('payment_type');
    const [bookingData, setBookingData] = useState<any>(null);
    const statusParam = searchParams.get('status');
    const isCancelled = statusParam === 'cancelled' || statusParam === 'rechazado';
    const isEn = t.success_title?.toLowerCase().includes('request');
    const isDeposit = typeParam === 'deposit' || bookingData?.payment_status === 'partially_paid';

    // Scroll to top on mount & trigger GA4 conversion events only on real success
    useEffect(() => {
        window.scrollTo(0, 0);

        if (!isCancelled && typeof window.gtag === 'function') {
            // Evento estándar de conversión de GA4
            window.gtag('event', 'generate_lead', {
                event_category: 'engagement',
                event_label: 'booking_success'
            });

            // Evento personalizado de conversión
            window.gtag('event', 'booking_completed', {
                value: 1.0,
                currency: 'USD'
            });
            console.log("GA4 Conversion events (generate_lead, booking_completed) triggered!");
        }

        // If returned from VirtualPOS, check booking status in Supabase
        if (provider === 'virtualpos' && bookingId) {
            const checkStatus = async () => {
                try {
                    const { data } = await supabase
                        .from('bookings')
                        .select('payment_status')
                        .eq('id', bookingId)
                        .single();

                    console.log("[Success] VirtualPOS Booking status:", data?.payment_status);
                    setBookingData(data);
                } catch (e) {
                    console.error("[Success] Error checking VirtualPOS status:", e);
                } finally {
                    setIsCheckingVirtualPos(false);
                }
            };
            checkStatus();
        }
    }, [provider, bookingId, isCancelled]);

    return (
        <div className="min-h-[85vh] flex items-center justify-center px-6 pt-20 pb-16">
            <div className="max-w-4xl w-full">
                <div className="bg-stone border border-rock p-8 md:p-16 text-center relative overflow-hidden group">
                    {/* Background Decorative Element */}
                    <div className="absolute top-0 right-0 w-64 h-64 bg-[#00d4ff]/5 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl group-hover:bg-[#00d4ff]/10 transition-colors duration-700"></div>

                    <div className="relative z-10 flex flex-col items-center">
                        {isCancelled ? (
                            /* PAGO NO COMPLETADO (CANCELADO / VOLVER) */
                            <>
                                {/* Warning Icon */}
                                <div className="w-20 h-20 bg-amber-500/10 border border-amber-500/30 flex items-center justify-center rounded-full mb-10 text-amber-400">
                                    <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                    </svg>
                                </div>

                                <h1 className="text-3xl md:text-6xl font-title uppercase tracking-tighter leading-none text-white mb-6">
                                    {isEn ? 'Payment Not Completed' : 'Pago No Completado'}
                                </h1>

                                <h2 className="text-amber-400 text-[10px] font-bold uppercase tracking-[0.5em] mb-12 border-y border-amber-400/20 py-4 w-full max-w-sm">
                                    {isEn ? 'No charge was made to your account' : 'No se ha realizado ningún cargo'}
                                </h2>

                                <div className="max-w-xl mx-auto space-y-6 mb-12">
                                    <p className="text-gray-300 text-base md:text-lg font-light leading-relaxed">
                                        {isEn 
                                            ? 'You clicked back or cancelled the payment process. Don’t worry: your booking details are saved in pending status so you can complete your reservation whenever you wish.'
                                            : 'Presionaste volver o cancelaste el proceso de pago en la pasarela. No te preocupes: los datos de tu expedición siguen guardados en estado pendiente para que puedas completar tu reserva cuando gustes.'
                                        }
                                    </p>

                                    <div className="h-px w-12 bg-rock/50 mx-auto"></div>

                                    <p className="text-[9px] text-gray-500 uppercase font-bold tracking-[0.3em]">
                                        Villa Cerro Castillo, Aysén, Chile
                                    </p>
                                </div>

                                <div className="flex flex-col sm:flex-row items-center justify-center gap-4 w-full max-w-md mb-8">
                                    <Link
                                        to={bookingId ? getLocalizedPath(`/registro?booking_id=${bookingId}`, lang) : getLocalizedPath('/expeditions', lang)}
                                        className="bg-[#00d4ff] text-black px-8 py-4 font-title text-base uppercase tracking-widest hover:bg-white transition-all transform hover:-translate-y-1 shadow-[0_10px_30px_-10px_rgba(0,212,255,0.4)] w-full sm:w-auto text-center"
                                    >
                                        {isEn ? 'Retry Payment →' : 'Reintentar Pago →'}
                                    </Link>
                                    <Link
                                        to={getLocalizedPath('/expeditions', lang)}
                                        className="border border-white/20 text-white hover:bg-white/10 px-8 py-4 font-title text-base uppercase tracking-widest transition-all w-full sm:w-auto text-center"
                                    >
                                        {isEn ? 'View Expeditions' : 'Ver Expediciones'}
                                    </Link>
                                </div>

                                <a 
                                    href="https://wa.me/56987290959?text=Hola,%20tengo%20una%20consulta%20sobre%20mi%20reserva%20en%20Fisura%20Expeditions"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs text-gray-400 hover:text-[#00d4ff] transition-colors flex items-center gap-1.5"
                                >
                                    <span>{isEn ? 'Need help? Chat with a guide on WhatsApp' : '¿Dudas con el pago? Chatea con un guía por WhatsApp'}</span>
                                    <span>→</span>
                                </a>
                            </>
                        ) : (
                            /* PAGO EXITOSO */
                            <>
                                {/* Success Icon/Indicator */}
                                <div className="w-20 h-20 bg-[#00d4ff]/10 border border-[#00d4ff]/30 flex items-center justify-center rounded-full mb-10 text-[#00d4ff]">
                                    <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                                    </svg>
                                </div>

                                <h1 className="text-4xl md:text-7xl font-title uppercase tracking-tighter leading-none text-white mb-6">
                                    {t.success_title}
                                </h1>

                                <h2 className="text-[#00d4ff] text-[10px] font-bold uppercase tracking-[0.5em] mb-12 border-y border-[#00d4ff]/20 py-4 w-full max-w-md">
                                    {isDeposit ? (isEn ? '50% Reservation Deposit Confirmed · Spot Secured' : 'Abono 50% Confirmado · Cupo Asegurado') : t.success_subtitle}
                                </h2>

                                <div className="max-w-xl mx-auto space-y-8 mb-16">
                                    <p className="text-gray-300 text-lg md:text-xl font-light italic leading-loose">
                                        {isDeposit 
                                            ? (isEn 
                                                ? 'Your spot has been successfully secured with your 50% reservation deposit. Exactly 48 hours prior to departure, you will receive an automatic email reminder with a secure payment link to complete the remaining 50% balance.' 
                                                : 'Tu cupo en la expedición ha sido asegurado con éxito mediante tu abono del 50%. Exactamente 48 horas antes de la salida, recibirás un correo automático recordatorio con el enlace de pago seguro para liquidar el 50% restante.') 
                                            : t.success_text}
                                    </p>

                                    <div className="h-px w-12 bg-rock/50 mx-auto"></div>

                                    <p className="text-[9px] text-gray-500 uppercase font-bold tracking-[0.3em]">
                                        Villa Cerro Castillo, Aysén, Chile
                                    </p>
                                </div>

                                <Link
                                    to={getLocalizedPath('/expeditions', lang)}
                                    className="bg-[#00d4ff] text-black px-12 py-5 font-title text-xl uppercase tracking-widest hover:bg-white transition-all transform hover:-translate-y-1 shadow-[0_10px_30px_-10px_rgba(0,212,255,0.4)]"
                                >
                                    {t.success_cta}
                                </Link>

                                {/* POLICY SECTION */}
                                <div className="mt-24 w-full max-w-2xl mx-auto border-t border-rock pt-12 space-y-4 opacity-80 hover:opacity-100 transition-opacity">
                                    <h3 className="text-[#00d4ff] text-[10px] font-bold uppercase tracking-[0.3em] text-left">Recordatorio Importante de tu Expedición</h3>
                                    <div className="bg-black/40 border border-[#00d4ff]/10 p-6 rounded-sm">
                                        <PolicyText />
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Success;
